import { useContext, useEffect, useReducer, useRef, useState } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'

import { useFeatureIsOn } from '@growthbook/growthbook-react'

import { useProductListingPageQuery } from '../../../generated/frontend'
import type { PageParams } from '../../../routes'
import { ErrorComponent, PageError } from '../../../shared/components/Error'
import { Loader } from '../../../shared/components/Loader'
import { CompareTrayContext } from '../../../shared/contexts/CompareTrayContext'
import { useFullUrl } from '../../../shared/hooks/useFullUrl'
import { useGetQueryString } from '../../../shared/hooks/useGetQueryString'
import { PageTemplate } from '../../../shared/templates/PageTemplate'
import { usePageProps } from '../../../shared/usePageProps'
import { getQueryString } from '../../../shared/utils'
import { isIPad, isIPhone, isSafari } from '../../../shared/utils/detect-device'
import { UPDATE_PRODUCT_LISTING_PAGE_DATA } from './constants/action-types'
import { useProductListingComponents } from './hooks/useProductListingComponents'
import { useProductListingPageFilters } from './hooks/useProductListingPageFilters'
import { useProductListingPageMetaTags } from './hooks/useProductListingPageMetaTags'
import styles from './ProductListingPage.module.scss'
import { initialState, productListingPageReducer } from './reducers/productListingPage.reducer'
import type { ReviewsAction, ReviewsState } from './ReviewsTemplateContext'
import { ReviewsTemplateContext } from './ReviewsTemplateContext'
import { getSelectedFilters } from './utils/get-selected-filters'

const ProductListingPage: React.FC = () => {
  const { setTaxonomySlug } = useContext(CompareTrayContext);
  const { categorySlug } = useParams<PageParams>()
  const { context } = usePageProps()
  const { getFullUrl } = useFullUrl()
  const fullUrl = getFullUrl()

  const [userAgent, setUserAgent] = useState('')

  useEffect(() => {
    setUserAgent(navigator.userAgent)
  }, [])

  useEffect(() => {
    setTaxonomySlug(categorySlug)
  }, [categorySlug, setTaxonomySlug])

  const {
    basicFilterData,
    needsBasedFilterData,
    filterCallback,
    setIsOverlayFiltersDisplayed,
    isOverlayFiltersDisplayed,
    showMoreFilters,
    setShowMoreFilters,
    selectedNeedsBasedFilters,
    pageNumberOnFilterInteraction,
    setPageNumberOnFilterInteraction,
  } = useProductListingPageFilters()

  const [state, dispatch] = useReducer<React.Reducer<ReviewsState, ReviewsAction>>(
    productListingPageReducer,
    initialState
  )

  const pageNumberFromQueryString = useGetQueryString('page')
  const { loading, error, data, fetchMore } = useProductListingPageQuery({
    variables: {
      categorySlug,
      context: context || '',
      pageNumber:
        (pageNumberOnFilterInteraction && String(pageNumberOnFilterInteraction)) ||
        pageNumberFromQueryString ||
        '1',
      sortBy: useGetQueryString('sortBy') || '',
      basicFilterData,
      needsBasedFilterData,
      selectedNeedsBasedFilters,
      channel: 'deliver',
    },
    notifyOnNetworkStatusChange: true,
  })

  const components = useProductListingComponents()
  const { getPageMetaTags } = useProductListingPageMetaTags()
  const { header, meta, attributesCount, filters, needsBasedFilters } = state.productListingPageData
  const initialDataFetched = useRef(false)

  /** Ensures data is dispatched to the store when rendering server side */
  if (!initialDataFetched.current && data?.productListingPage) {
    dispatch({
      type: UPDATE_PRODUCT_LISTING_PAGE_DATA,
      payload: {
        data: {
          productListingPageData: data.productListingPage,
          selectedFilters: getSelectedFilters({
            basicFilters: data.productListingPage.filters,
            basicFilterData,
            selectedNeedsBasedFilters,
            needsBasedFilters: data.productListingPage.needsBasedFilters,
            needsBasedFilterData,
          }),
          fullUrl,
          filterContext: {
            basicFilterData,
            needsBasedFilterData,
            selectedNeedsBasedFilters,
            pageNumberOnFilterInteraction,
            setPageNumberOnFilterInteraction,
            filterCallback,
            isOverlayFiltersDisplayed,
            setIsOverlayFiltersDisplayed,
            showMoreFilters,
            setShowMoreFilters,
          },
          setToastContext: state.setToastContext,
        },
      },
    })
    initialDataFetched.current = true
  }

  useDeepCompareEffect(() => {
    if (data?.productListingPage && initialDataFetched.current) {
      dispatch({
        type: UPDATE_PRODUCT_LISTING_PAGE_DATA,
        payload: {
          data: {
            productListingPageData: data.productListingPage,
            selectedFilters: getSelectedFilters({
              basicFilters: filters,
              basicFilterData,
              selectedNeedsBasedFilters,
              needsBasedFilters: needsBasedFilters,
              needsBasedFilterData,
            }),
            fullUrl,
            filterContext: {
              basicFilterData,
              needsBasedFilterData,
              selectedNeedsBasedFilters,
              pageNumberOnFilterInteraction,
              setPageNumberOnFilterInteraction,
              filterCallback,
              isOverlayFiltersDisplayed,
              setIsOverlayFiltersDisplayed,
              showMoreFilters,
              setShowMoreFilters,
            },
            setToastContext: state.setToastContext,
          },
        },
      })
    }
  }, [
    basicFilterData,
    data,
    needsBasedFilterData,
    selectedNeedsBasedFilters,
    filters,
    needsBasedFilters,
    fullUrl,
    isOverlayFiltersDisplayed,
    setIsOverlayFiltersDisplayed,
    showMoreFilters,
    setShowMoreFilters,
  ])

  const pageLimit = state.productListingPageData.productsWithPagination?.numberOfPages
  const currentPage = Number(getQueryString('page'))

  if (currentPage > pageLimit || currentPage < 0) {
    const redirectUrl = new URL(fullUrl)

    currentPage > pageLimit
      ? redirectUrl.searchParams.set('page', String(pageLimit))
      : redirectUrl.searchParams.delete('page')

    return <Redirect to={`${redirectUrl.pathname}${redirectUrl.search}`} />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (!data?.productListingPage && !loading) {
    return <PageError pageName="Product Listing Page" />
  }

  const hasVariants = Boolean(
    data?.productListingPage?.productsWithPagination?.products?.find(
      (product) => product.variantProducts?.length
    )
  )

  const metaTags = () =>
    getPageMetaTags({
      meta,
      imageUrl: header?.image?.src || '',
      imageAlt: header?.image?.alt || '',
      twitterCard: 'summary_large_image',
      attributesCount,
      appliedFilters: state.selectedFilters,
      fullUrl: state.fullUrl,
      hasVariants,
    })

  // Rendered when user is on IOS device, using Safari.
  // TODO: Remove function and implement as default returned if variant wins the experiment.
  const ProductListingPageWithSmartBanner = () => {
    // Check feature flag to bucket user into a variant.
    const displayIosSmartBanner = useFeatureIsOn('smart-banner-reviews')

    // Return metaTags including Smart Banner if user is in experiment variant.
    // Return default metaTags if user is not in experiment variant.
    const metaTagsByVariant = displayIosSmartBanner
      ? [
          ...metaTags(),
          <meta
            name="apple-itunes-app"
            content={`app-id=1082710781, app-argument=which://content/products/${meta.taxonomyHierarchy?.category?.id}`}
          />,
        ]
      : metaTags()

    // Return page template with metaTags dynamically created depending on experiment status.
    return (
      <PageTemplate
        metaTags={metaTagsByVariant}
        components={components}
        showResubBanner={data?.productListingPage.showResubBanner}
      />
    )
  }

  // Rendered when user is not on IOS device and/or not using Safari
  // TODO: Remove function and implement as default returned if control wins the experiment.
  const ProductListingPageWithoutSmartBanner = () => (
    <PageTemplate
      metaTags={metaTags()}
      components={components}
      showResubBanner={data?.productListingPage.showResubBanner}
    />
  )

  const isSafariIOS = isSafari(userAgent) && (isIPhone(userAgent) || isIPad(userAgent))

  // Handles conditionally rendering PageTemplate with/Without banner
  // TODO: Remove and return the winning variant directly depending on which variant wins the experiment.
  const renderPageTemplate = () => {
    return isSafariIOS ? (
      <ProductListingPageWithSmartBanner />
    ) : (
      <ProductListingPageWithoutSmartBanner />
    )
  }

  return (
    <ReviewsTemplateContext.Provider
      value={{
        fetchMore,
        state,
        dispatch,
        pageNumberOnFilterInteraction,
        setPageNumberOnFilterInteraction,
      }}
    >
      {loading && <Loader className={styles.reviewsProductsLoader} />}
      {state.productListingPageData &&
        Object.keys(state.productListingPageData).length > 0 &&
        renderPageTemplate()}
    </ReviewsTemplateContext.Provider>
  )
}

export default ProductListingPage
